/**
 * Recovers from stale-deploy chunk-load failures.
 *
 * After a deploy, asset filename hashes change. A user holding a cached
 * index.html dynamically imports route chunks that no longer exist; the
 * server's SPA fallback then returns index.html (text/html) instead of
 * JavaScript. Vite emits a `vite:preloadError` event for these failures
 * (ORDER-THISWAY-10 / 11 / 1T / 1Z).
 *
 * We reload the page once so the browser fetches a fresh index.html.
 * A sessionStorage timestamp guards against an infinite reload loop: if a
 * reload already happened within RELOAD_WINDOW_MS, we do NOT reload again
 * and do NOT suppress the error — a failure on the freshly loaded page
 * means the chunk is genuinely broken, not stale, and should stay visible
 * in Sentry.
 *
 * Vite fires one `vite:preloadError` per failed dependency of an import
 * (each CSS file, then the module itself), so one stale import can raise
 * several in a row on the same page. Once this page has scheduled its
 * reload, the later ones are the same failure: they are swallowed too
 * (ORDER-THISWAY-2E / 36). When the module itself failed and its event is
 * prevented, the import resolves with `undefined`, which `lazyRoute` keeps
 * pending until the reload lands.
 */
const RELOAD_FLAG_KEY = 'chunk-reload-ts';
const RELOAD_WINDOW_MS = 10_000;

// sessionStorage access can throw a SecurityError in private-browsing modes
// (notably Safari private mode). Wrap it so the guard never crashes — at
// worst it falls back to attempting one reload per page load.
function readReloadTimestamp() {
  try {
    return Number(sessionStorage.getItem(RELOAD_FLAG_KEY) || 0);
  } catch {
    return 0;
  }
}

function writeReloadTimestamp(ts) {
  try {
    sessionStorage.setItem(RELOAD_FLAG_KEY, String(ts));
  } catch {
    // best-effort — if storage is unavailable, the loop guard degrades
    // gracefully to one reload attempt per page load.
  }
}

// Returns a function that removes the listener. main.jsx installs the guard
// once per page load and never removes it; the tests do, so one test's guard
// does not answer the next test's events.
export function installChunkReloadGuard() {
  // Per page load: main.jsx calls this once, and the reload replaces the page.
  let reloadScheduled = false;

  const onPreloadError = (event) => {
    // Checked before the timestamp: the timestamp this page just wrote would
    // otherwise read as "reloaded recently" and let the later errors through.
    if (reloadScheduled) {
      event.preventDefault();
      return;
    }

    const last = readReloadTimestamp();
    const now = Date.now();

    if (now - last < RELOAD_WINDOW_MS) {
      // Already reloaded recently — the chunk is genuinely broken.
      // Do NOT preventDefault: let Vite re-throw so Sentry records it.
      return;
    }

    // Stale deploy: suppress Vite's re-throw and reload to get a fresh
    // index.html with up-to-date asset hashes.
    event.preventDefault();
    reloadScheduled = true;
    writeReloadTimestamp(now);
    window.location.reload();
  };

  window.addEventListener('vite:preloadError', onPreloadError);
  return () => window.removeEventListener('vite:preloadError', onPreloadError);
}
